import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TextLinkComponent, ThemedLinkStyle, generateLinkStyleFromTheme } from '../../partials/link/TextLink';
import { useTheme } from '../../../theming/useTheme';
import { VrLineHeight } from '../../../theming/verticalRhythm/verticalRhythm';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';

const componentConfigurationKey = 'sectionHeader';
const createStyles = (vrLineHeight: VrLineHeight, titleColor: string, marginTop: number) => StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * marginTop}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * marginTop}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * marginTop}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * marginTop}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
    },
  },
  titleItem: {
    flex: '1 1 auto',
    color: titleColor,
  },
});

const createLinkTextStyles = () => StyleSheet.create({
  linkText: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      ':hover': {
        textDecoration: 'underline',
      },
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      ':hover': {
        textDecoration: 'underline',
      },
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      ':hover': {
        color: 'initial',
      },
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      ':hover': {
        color: 'initial',
      },
    },

  },
});

const createLink = ({ linkText,
  idleColor,
  activeStateColor,
  hoverStateColor,
  href,
}: SectionHeaderDataProps & ThemedLinkStyle) => {
  if (href && linkText) {
    const linkStyle = generateLinkStyleFromTheme({
      idleColor,
      activeStateColor,
      hoverStateColor,
    });
    const style = createLinkTextStyles();

    return (
      <TextLinkComponent href={href} linkStyle={linkStyle}>
        <h4 className={css(style.linkText)}>{linkText}</h4>
      </TextLinkComponent>
    );
  }
  return null;
};

interface SectionHeaderComponentThemeProps {
  vrLineHeight: VrLineHeight;
  titleColor: string;
  marginTop?: number;
}

interface SectionHeaderDataProps {
  linkText: string | null;
  href: string | null
}


type SectionHeaderComponentProps = SectionHeaderComponentThemeProps & SectionHeaderDataProps & ThemedLinkStyle;

export const SectionHeaderComponent: React.FunctionComponent<SectionHeaderComponentProps> = ({
  linkText,
  vrLineHeight,
  titleColor,
  idleColor = '',
  activeStateColor = '',
  hoverStateColor = '',
  href,
  marginTop = 1,
  children,
}) => {
  const style = createStyles(vrLineHeight, titleColor, marginTop);

  return (
    <div className={css(style.container)}>
      <div className={css(style.titleItem)}>
        {children}
      </div>
      {createLink({
        linkText,
        idleColor,
        activeStateColor,
        hoverStateColor,
        href,
      })}
    </div>
  );
};

const mapThemeProps = (theme: SectionHeaderComponentThemeProps & ThemedLinkStyle) => ({
  vrLineHeight: theme.vrLineHeight,
  titleColor: theme.titleColor,
  idleColor: theme.idleColor,
  activeStateColor: theme.activeStateColor,
  hoverStateColor: theme.hoverStateColor,
});

export const SectionHeader:React.FunctionComponent<SectionHeaderDataProps> = props => {
  const themeContext = useTheme(componentConfigurationKey, mapThemeProps);
  return <SectionHeaderComponent {...themeContext} {...props} />;
};
